import React from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { tokenTrend, topConsumers } from "@/data/mockData";
import AnimatedNumber from "./AnimatedNumber";

const CTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-[#1A2238]/95 px-3 py-2 text-xs backdrop-blur-md">
      <div className="text-slate-400">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="font-mono text-slate-200">
          {p.name}: <span style={{ color: p.color }}>{Number(p.value).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

function Stat({ label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">{label}</div>
      <div className="mt-2 font-mono text-2xl font-light tracking-tight text-white">{value}</div>
      {sub && <div className="mt-1 text-[11px] text-slate-500">{sub}</div>}
    </div>
  );
}

export default function TokenIntelligence({ stats }) {
  return (
    <div className="card-eco p-6" data-testid="token-intelligence-widget">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
            Token Intelligence
          </div>
          <div className="mt-1 font-display text-lg text-slate-100">
            Spend, throughput & anomaly detection
          </div>
        </div>
        <span className="rounded-full border border-white/5 bg-white/[0.03] px-2.5 py-1 font-mono text-[11px] text-slate-300">
          last 14 days
        </span>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Today" value={stats.today} sub="tokens" testid="token-stat-today" />
        <Stat label="This month" value={stats.monthly} sub="tokens" testid="token-stat-monthly" />
        <Stat label="Est. cost" value={stats.cost} sub="MTD" testid="token-stat-cost" />
        <Stat
          label="Anomalies"
          value={<AnimatedNumber value={stats.anomalies} />}
          sub="last 24h"
          testid="token-stat-anomalies"
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <div className="mb-2 text-[11px] uppercase tracking-[0.18em] text-slate-400">
            Usage trend
          </div>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={tokenTrend} margin={{ left: 0, right: 0, top: 6, bottom: 0 }}>
                <defs>
                  <linearGradient id="tokFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#5B8DEF" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#5B8DEF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis dataKey="day" stroke="#475569" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                <YAxis stroke="#475569" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={36} tickFormatter={(v) => `${Math.round(v / 1000)}K`} />
                <Tooltip content={<CTooltip />} cursor={{ stroke: "rgba(91,141,239,0.4)", strokeWidth: 1 }} />
                <Area type="monotone" dataKey="tokens" name="Tokens" stroke="#5B8DEF" strokeWidth={1.8} fill="url(#tokFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="lg:col-span-2">
          <div className="mb-2 text-[11px] uppercase tracking-[0.18em] text-slate-400">
            Top consumers
          </div>
          <div className="h-[180px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topConsumers} layout="vertical" margin={{ left: 0, right: 8, top: 0, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" horizontal={false} />
                <XAxis type="number" hide />
                <YAxis dataKey="name" type="category" stroke="#94a3b8" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={110} />
                <Tooltip content={<CTooltip />} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
                <Bar dataKey="tokens" name="Tokens" radius={[0, 6, 6, 0]} barSize={14}>
                  {topConsumers.map((c, i) => (
                    <Cell key={i} fill={c.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
